import { ImageMask } from "./ImageMask";
export enum ELightPath {
  Disabled = -1,
  Visible = 0,
  Ultraviolet = 1,
  Sonicc = 2,
  Frap = 3,
  Fluorescence = 4,
}

export enum EDropLocationMethod {
  Fixed = 0,
  Standard = 1,
  LipidPhase = 2,
  Fluorescence = 3,
}

export enum EAutoFocusMethod {
  Unknown = 0,
  AutoFocusWithLimits = 1,
  FixedRangeDoF = 2,
  FixedRangeInterpolated = 3,
}

export enum EAutoExposureMethod {
  Fixed = 2,
  Plate = 1,
  Drop = 0,
}

export interface DropLocationCaptureProperty {
  dropLocationMethod: EDropLocationMethod;
  missedDropZoom: number;
  saveDropLocation: boolean;
  dropOptionOverrideExist: boolean;
}

export interface FixedRangeDofLimitedCaptureProperty {
  imagingZmax: number;
  imagingZmin: number;
}

export interface FixedRangeInterpolatedCaptureProperty {
  imagingZmax: number;
  imagingZmin: number;
  maxFocusLevels: number;
}

export interface AutoFocusCaptureProperty {
  focusThresholdAbove: number;
  focusThresholdBelow: number;
  maxFocusLevelsAbove: number;
  maxFocusLevelsBelow: number;
}

export interface FocusCaptureProperty {
  autoFocusMethod: EAutoFocusMethod;
  depthOfFieldStepSizePercentage: number;
  minAutoFocusStepSize: number;
  fixedRangeDofLimited: FixedRangeDofLimitedCaptureProperty;
  fixedRangeInterpolated: FixedRangeInterpolatedCaptureProperty;
  autoFocus: AutoFocusCaptureProperty;
  focusLevelCaptureMethod: number;
}

export interface Roi {
  autoSelectObjective: boolean;
  fixedObjective: number;
}

export interface AutoFocusAlgorithmProperty {
  autoFocusZmin: number;
  autoFocusZmax: number;
  focusFrameRate: number;
  focusMoveVelocity: number;
}

export interface StandardDropLocatorCaptureProperty {
  minLengthMicrons: number;
  maxLengthMicrons: number;
  whitePixelMin: number;
  whitePixelMax: number;
  iterations: number;
  maxAspectRatio: number;
}

export interface LipidPhaseDropLocatorCaptureProperty {
  minDropSizeInMicrons: number;
  maxDropSizeInMicrons: number;
  dropWhitePixelMin: number;
  dropWhitePixelMax: number;
  dropOpenIterations: number;
  lipidPhaseBinarizerWhitePixelMin: number;
  lipidPhaseBinarizerWhitePixelMax: number;
  lipidPhaseOpenIterations: number;
}

export interface LocateDropAlgorithmProperty {
  dropLocationCondenser: number;
  dropLocationGamma: number;
  dropLocationPolarizer: number;
  dropLocationBinning: number;
  dropLocationZoom: number;
  standardDropLocator: StandardDropLocatorCaptureProperty;
  lipidPhaseDropLocator: LipidPhaseDropLocatorCaptureProperty;
}

export interface VisibleCaptureProperty {
  autoExposure?: AutoExposureAlgorithmProperty;
  autoFocus?: AutoFocusAlgorithmProperty;
  locateDrop?: LocateDropAlgorithmProperty;
  exposure?: number;
  actualExposure?: number;
  brightness?: number;
  gain?: number;
  gamma?: number;
  binning?: number;
  continuousZoom?: number;
  cropZoom?: number;
  condenser?: number;
  polarizer?: number;
  objectiveZoom?: number;
}

export interface AutoExposureAlgorithmProperty {
  autoExposureMethod: EAutoExposureMethod;
  minExposure: number;
  maxExposure: number;
}

export interface VisibleFluorescenceCaptureProperty {
  autoExposure?: AutoExposureAlgorithmProperty;
  exposure?: number;
  actualExposure?: number;
  brightness?: number;
  gain?: number;
  gamma?: number;
}

export interface UltravioletFluorescenceCaptureProperty {
  exposure?: number;
  actualExposure?: number;
  gain?: number;
  gamma?: number;
  illumination?: boolean;
}

export interface FluorescenceCaptureProperty {
  visibleFluorescence?: VisibleFluorescenceCaptureProperty;
  ultravioletFluorescence?: UltravioletFluorescenceCaptureProperty;
  binning?: number;
  cropZoom?: number;
  objectiveZoom?: number;
  filterCubeId?: number;
}

export interface UltravioletCaptureProperty {
  exposure?: number;
  actualExposure?: number;
  gain?: number;
  gamma?: number;
  binning?: number;
  cropZoom?: number;
  objectiveZoom?: number;
  illumination?: boolean;
}

export interface DropOverride {
  roi?: Roi;
  continuousZoom?: number;
  cropZoom?: number;
  objectiveZoom?: number;
}

export interface CaptureProfile {
  lightpath: ELightPath;
  dropLocation?: DropLocationCaptureProperty;
  focus?: FocusCaptureProperty;
  roi?: Roi;
  visible?: VisibleCaptureProperty;
  fluorescence?: FluorescenceCaptureProperty;
  ultraviolet?: UltravioletCaptureProperty;
  plateMaskEnabled?: boolean;
  imageMask?: ImageMask;
  dropOverrides?: DropOverride[];
}

export interface PlateTypeCaptureProfile extends CaptureProfile {
  imagerId: string;
  idPlateType: number;
  enabled: boolean;
}

export interface CaptureProfileInfo {
  captureProfileId: string;
  name: string;
}


export interface ImagingCaptureProfile extends CaptureProfile {
  captureProfileId: string;
  name: string;
}

export interface ImagingCaptureProfileIdAndName{
  captureProfileId: string;
  name: string;
}

export interface PsrAutoFocus {
  useAutoFocus: boolean;
}

export interface PsrAutoExposure {
  useAutoExposure: boolean;
}

export interface PsrCaptureProfile {
  autoFocus?: PsrAutoFocus;
  autoExposure?: PsrAutoExposure;
  focus?: number;
  exposure?: number;
  brightness?: number;
  gain?: number;
  binning?: number;
}

export interface NamedPsrCaptureProfile extends PsrCaptureProfile{
  id: number,
  captureProfileName: string,
}
